import React, {useMemo} from 'react'
import {createUseStyles} from 'react-jss'
import {Colors} from '../../constants/colors'
import {ComplianceStatus, UserListItemModel} from '../../redux/types'
import {ColumnDef, flexRender, getCoreRowModel, useReactTable} from '@tanstack/react-table'
import {useNavigate} from 'react-router-dom'
import Pagination from '../Pagination'
import {Badge} from "react-bootstrap";

interface Props {
  usersOnPage: UserListItemModel[]
  pageIndex: number
  pageSize: number
  totalCount: number
  onPageChange: (pageIndex: number) => void
}

const UserList = ({usersOnPage, pageIndex, pageSize, totalCount, onPageChange}: Props) => {
  const styles = useStyles()
  const navigate = useNavigate()

  const columns = useMemo<ColumnDef<UserListItemModel>[]>(() => [
    {
      id: 'name',
      header: 'Name',
      cell: ({row}) => <span>{`${row.original.givenName} ${row.original.familyName}`}</span>,
    },
    {
      id: 'identificationNumber',
      header: 'Identification number',
      cell: ({row}) => <span>{row.original.identificationNumber}</span>,
    },
    {
      id: 'state',
      header: 'State',
      cell: ({row}) => (
        <div style={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', fontSize: 16, gap: 4}}>
          <Badge style={{textTransform: 'capitalize'}} bg={"secondary"}>{row.original.state}</Badge>
          {row.original.complianceStatus === ComplianceStatus.nonCompliant &&
              <Badge text="dark" bg={"danger"}>{'Non-Compliant'}</Badge>
          }
          {row.original.complianceStatus === ComplianceStatus.unknown &&
              <Badge text="dark" bg={"warning"}>{'Missing compliance info'}</Badge>
          }
        </div>
      ),
    }
  ], [])

  const table = useReactTable({
    data: usersOnPage ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <div className={styles.container}>
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className={styles.headerRow}>
              {headerGroup.headers.map((header) => (
                <th key={header.id} className={styles.headerCell}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
          </thead>
          <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              className={styles.bodyRow}
              onClick={() => navigate(`/users/${row.original.id}`)}
              tabIndex={0}
            >
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className={styles.bodyCell}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
          </tbody>
        </table>
      </div>
      <div className={styles.pagination}>
        <Pagination
          pageIndex={pageIndex}
          itemCount={totalCount}
          pageSize={pageSize}
          siblingCount={1}
          onPageChange={onPageChange}
        />
      </div>
    </div>
  )
}

export default UserList

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  tableContainer: {
    overflow: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: '0 0px',
    fontSize: '14px',
  },
  headerRow: {
    backgroundColor: 'transparent',
  },
  headerCell: {
    padding: '6px 12px',
    textAlign: 'left',
    backgroundColor: Colors.darkGrayCard,
    borderRadius: '4px',
    fontWeight: 'bold',
    color: Colors.darkGray2,
    '&:first-child': {
      borderTopLeftRadius: '6px',
      borderBottomLeftRadius: '6px',
    },
    '&:last-child': {
      borderTopRightRadius: '6px',
      borderBottomRightRadius: '6px',
    },
  },
  bodyRow: {
    backgroundColor: Colors.darkGrayCard,
    cursor: 'pointer',
    transition: '0.4s',
    '&:hover': {
      backgroundColor: Colors.darkGrayHover,
    },
  },
  bodyCell: {
    padding: '2px 12px',
    textAlign: 'left',
    '&:first-child': {
      borderTopLeftRadius: '6px',
      borderBottomLeftRadius: '6px',
    },
    '&:last-child': {
      borderTopRightRadius: '6px',
      borderBottomRightRadius: '6px',
    },
  },
  pagination: {
    marginTop: 10,
    display: 'flex',
    justifyContent: 'center',
  },
})
